"use client";
import { useEffect, useRef } from "react";
import { PropsWithChildren } from "react";
import { Provider } from "react-redux";
import { AppStore, makeStore } from "@/redux/store";
import { GetCartQuery, SCartDto } from "@/__generated__/sdk";
import { getCart, updateCart } from "@/redux/features/cart/cartSlice";
import { Session } from "next-auth";
import { setSession } from "@/redux/features/session/sessionSlice";
import { useSession } from "next-auth/react";

type Props = {
  cart: SCartDto | null;
  session: Session | null;
} & PropsWithChildren;
export default function ReduxProvider({ children, cart, session }: Props) {
  const hasMounted = useRef(false);
  const { data, status } = useSession();
  const storeRef = useRef<AppStore>();
  if (!storeRef.current) {
    // Create the store instance the first time this renders
    storeRef.current = makeStore();
  }
  if (storeRef.current && !hasMounted.current) {
    storeRef.current.dispatch(updateCart(cart));
  }
  if (session) {
    storeRef.current.dispatch(
      setSession({
        session,
        status: "authenticated",
      }),
    );
  }

  useEffect(() => {
    if (storeRef.current) {
      storeRef.current.dispatch(
        setSession({
          session: data,
          status,
        }),
      );
    }
  }, [data, status, session]);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    if (storeRef.current) {
      storeRef.current.dispatch(getCart());
    }
  }, [data?.user?.me.id]);

  return <Provider store={storeRef.current}>{children}</Provider>;
}
